import React from "react";
import { useLanguage } from "../../hooks/use-language";


/**
 * Componente que muestra los botones de paginación, las props que recibe son las funciones que se retornan en el hook de usePagination;
 * @param {{title:string, onClickPrev:Function, onClickNext:Function, onClickPage:Function, currentPage:number, totalPages:number, range:Array<number|string>}} props
 * @prop {string} title - Titulo del paginador.
 * @prop {Function} onClickPrev - Funci n que se ejecuta al hacer clic en el bot n de "Anterior".
 * @prop {Function} onClickNext - Funci n que se ejecuta al hacer clic en el bot n de "Siguiente".
 * @prop {Function} onClickPage - Funci n que se ejecuta al hacer clic en un n mero de p gina.
 * @prop {number} currentPage - N mero de la p gina actual.
 * @prop {number} totalPages - N mero total de p ginas.
 * @prop {Array<number|string>} range - Arreglo que contiene los n meros de p gina a mostrar.
 * @returns {JSX.Element} Elemento JSX que representa los botones de paginaci n.
 */
export const PaginationControls = ({
	title,
	onClickPrev,
	onClickNext,
	onClickPage,
	currentPage,
	totalPages,
	range,
}) => {
	const lang = useLanguage();
	const handlePrevClick = () => {
		onClickPrev && onClickPrev();
	};
	const handleNextClick = () => {
		onClickNext && onClickNext();
	};

	const handlePageClick = (page) => {
		onClickPage && onClickPage(page);
	};

	const iconSize = 12;

	const defaultTitle = {
		es: "Navegación por páginas",
		en: "Page navigation",
	}

	const nextAriaLabel = {
		es: "Siguiente Página",
		en: "Next Page",
	}

	const prevAriaLabel = {
		es: "Página Anterior",
		en: "Previous Page",
	}

const currentPageAriaLabel = {
  es: (page) => `Página actual, página ${page}`,
  en: (page) => `Current page, page ${page}`,
};

const pageAriaLabel = {
  es: (page) => `Ir a la página ${page}`,
  en: (page) => `Go to page ${page}`,
};


	const rangeButtons = () => {
		return (
			<div className="d-flex justify-center align-items-center">
				<p className="sr-only" role="status" aria-live="polite">{currentPageAriaLabel[lang](currentPage)}</p>
				{range.map((page, i) => {
					const isNavigation = page === "..." ? false: true;
					const isCurrentPage = page === currentPage ? true : false;
					if(isNavigation){
					return <button
						type="button"
						className={`focusable-red pagination-button  ${isCurrentPage ? "selected" : ""}`}
						key={i}
						aria-current={isCurrentPage ? "page" : false}
						onClick={() => handlePageClick(page)}
						aria-label={`${isCurrentPage ? currentPageAriaLabel[lang](page) : pageAriaLabel[lang](page)}`}
					>
						<span aria-hidden="true">
						{page}
						</span>
					</button>	
					}
					return <span
						aria-hidden="true"
						className={`focusable-red pagination-button ellipsis`}
						key={i}
					>
						{page}
					</span>
					
				})}
			</div>
		);
	};

	return (
		<nav className="default-pagination-controls w-100 d-flex flex-column flex-md-row" aria-label={title || defaultTitle[lang]}>
			<div className="sm-mobile-range-buttons">{rangeButtons()}</div>
			<div className="w-100 d-flex justify-content-between justify-content-lg-center justify-content-center my-2">
				<button
					type="button"
					className="pagination-button nav-button focusable-red mr-4"
					onClick={() => handlePrevClick()}
					aria-label={prevAriaLabel[lang]}
					disabled={currentPage === 1 ? true : false}
				>
					<svg
						aria-hidden="true"
						width={iconSize}
						viewBox="0 0 8 13"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M6.5 1.5L1.5 6.5L6.5 11.5"
							stroke="currentColor"
							stroke-width="2.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
				<div className="range-buttons">{rangeButtons()}</div>

				<button
					type="button"
					className="pagination-button nav-button focusable-red ml-4"
					onClick={() => handleNextClick()}
					aria-label={nextAriaLabel[lang]}
					disabled={currentPage === totalPages}
				>
					<svg
						aria-hidden="true"
						width={iconSize}
						viewBox="0 0 9 14"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M2 12L7 7L2 2"
							stroke="currentColor"
							stroke-width="2.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>
		</nav>
	);
};
